package org.zq.astrock_ai.ui.research.gushi.components

import org.zq.astrock_ai.util.formatDecimal

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.delay
import org.zq.astrock_ai.api.ChartPeriod
import org.zq.astrock_ai.api.dto.QuantScanItemDto
import org.zq.astrock_ai.network.EastMoneyMarketContextClient
import org.zq.astrock_ai.network.EastMoneyNewsClient
import org.zq.astrock_ai.network.MarketRepository
import org.zq.astrock_ai.network.StockMarketContext
import kotlin.math.max
import kotlin.math.min
import org.zq.astrock_ai.quant.BuyZonePlan
import org.zq.astrock_ai.quant.SellZonePlan
import org.zq.astrock_ai.quant.ZoneMarketContext
import org.zq.astrock_ai.quant.buildBuyZonePlan
import org.zq.astrock_ai.quant.buildSellZonePlan
import org.zq.astrock_ai.quant.toQuantBars
import org.zq.astrock_ai.network.StockNewsCategory
import org.zq.astrock_ai.ui.research.gushi.data.DemoData
import org.zq.astrock_ai.ui.research.gushi.data.recommendMarkersFor
import org.zq.astrock_ai.ui.research.gushi.data.StockTrendMetrics
import org.zq.astrock_ai.ui.research.gushi.data.toCandles
import org.zq.astrock_ai.ui.research.gushi.data.withLatestNews
import org.zq.astrock_ai.ui.research.gushi.data.withMarketContext
import org.zq.astrock_ai.ui.research.gushi.model.Candle
import org.zq.astrock_ai.ui.research.gushi.model.HoldingPosition
import org.zq.astrock_ai.ui.research.gushi.model.NewsItem
import org.zq.astrock_ai.ui.research.gushi.model.NewsType
import org.zq.astrock_ai.ui.research.gushi.model.StockItem
import org.zq.astrock_ai.ui.research.gushi.model.TrendDirection
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.isPhoneWidth
import kotlin.math.abs

@Composable
fun ResearchPanel(
    stock: StockItem,
    metrics: StockTrendMetrics? = null,
    isLive: Boolean = false,
    holding: HoldingPosition? = null,
    isInWatchlist: Boolean = true,
    canRemoveWatch: Boolean = true,
    scan: QuantScanItemDto? = null,
    recommendReasons: List<String> = emptyList(),
    recommendRisks: List<String> = emptyList(),
    tradeMarkers: List<org.zq.astrock_ai.ui.research.gushi.model.ChartTradeMarker> = emptyList(),
    recommendHistory: List<org.zq.astrock_ai.ui.research.gushi.data.RecommendHistorySnapshot> = emptyList(),
    onAddHolding: () -> Unit = {},
    onAddWatch: () -> Unit = {},
    onRemoveWatch: () -> Unit = {},
    onSetAlert: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val demoMetrics = remember(stock.code) { DemoData.trendMetrics(stock) }
    val baseMetrics = metrics ?: demoMetrics
    val newsClient = remember { EastMoneyNewsClient() }
    val contextClient = remember { EastMoneyMarketContextClient() }
    var latestNews by remember(stock.code) { mutableStateOf<List<NewsItem>>(emptyList()) }
    var newsLoading by remember(stock.code) { mutableStateOf(true) }
    var newsError by remember(stock.code) { mutableStateOf(false) }
    var marketContext by remember(stock.code) { mutableStateOf(StockMarketContext()) }
    var localSellPlan by remember(stock.code) { mutableStateOf<SellZonePlan?>(null) }
    var localBuyPlan by remember(stock.code) { mutableStateOf<BuyZonePlan?>(null) }
    val marketRepository = remember { MarketRepository() }
    val resolvedMetrics = remember(baseMetrics, marketContext, latestNews) {
        baseMetrics
            .withMarketContext(marketContext)
            .withLatestNews(latestNews)
    }
    // 本地含资金/公告微调时优先；否则回退扫描区间
    val sellPlan = remember(scan, localSellPlan, stock.price) {
        localSellPlan?.takeIf { it.low > 0 && it.high > 0 }
            ?: resolveResearchSellPlan(scan, null)
    }
    val buyPlan = remember(scan, localBuyPlan, stock.price) {
        localBuyPlan?.takeIf { it.low > 0 && it.high > 0 }
            ?: resolveResearchBuyPlan(scan, null)
    }
    val sellZone = sellPlan?.let { it.low to it.high }
    val buyZone = buyPlan?.let { it.low to it.high }

    LaunchedEffect(stock.code) {
        while (true) {
            val result = runCatching {
                newsClient.fetchLatest(stock.code).map { entry ->
                    NewsItem(
                        time = entry.publishedAt.toDisplayTime(),
                        type = when (entry.category) {
                            StockNewsCategory.NEWS -> NewsType.NEWS
                            StockNewsCategory.CAPITAL -> NewsType.CAPITAL
                            StockNewsCategory.ANNOUNCEMENT -> NewsType.ANNOUNCEMENT
                            StockNewsCategory.FINANCIAL -> NewsType.FINANCIAL
                            StockNewsCategory.RESEARCH -> NewsType.RESEARCH
                        },
                        title = entry.title,
                        source = entry.source,
                        impact = "实时",
                        url = entry.url,
                    )
                }
            }
            result.onSuccess { liveItems ->
                latestNews = liveItems
                newsError = false
            }.onFailure {
                newsError = true
            }
            newsLoading = false
            delay(60_000)
        }
    }

    LaunchedEffect(stock.code) {
        while (true) {
            runCatching { contextClient.fetch(stock.code) }
                .onSuccess { marketContext = it }
            delay(60_000)
        }
    }

    // 买卖区：不把现价/全文新闻标题放进 key，避免报价刷新时反复重算打断滑动
    val newsHeadlineKey = remember(latestNews) {
        latestNews.take(8).joinToString("|") { it.title.take(24) }
    }
    LaunchedEffect(
        stock.code,
        scan?.signal,
        marketContext.todayMainNet,
        marketContext.recentMainNet,
        newsHeadlineKey,
    ) {
        runCatching {
            val bars = marketRepository.loadKlineBars(stock.code, limit = 120).toQuantBars()
            val px = stock.price.replace(",", "").toDoubleOrNull()
                ?: scan?.price?.takeIf { it > 0 }
                ?: 0.0
            val signal = scan?.signal ?: "HOLD"
            val zoneMarket = ZoneMarketContext(
                todayMainNet = marketContext.todayMainNet,
                recentMainNet = marketContext.recentMainNet,
                headlines = latestNews.map { it.title }.filter { it.isNotBlank() }.take(16),
            )
            localSellPlan = buildSellZonePlan(bars, px, signal, market = zoneMarket)
                .takeIf { it.low > 0 && it.high > 0 }
            localBuyPlan = buildBuyZonePlan(bars, px, signal, market = zoneMarket)
                .takeIf { it.low > 0 && it.high > 0 }
        }
    }

    val researchReasons = remember(
        stock,
        resolvedMetrics,
        scan,
        recommendReasons,
        marketContext,
        buyZone,
        sellZone,
        buyPlan,
        sellPlan,
    ) {
        buildResearchReasons(
            stock = stock,
            metrics = resolvedMetrics,
            scan = scan,
            recommendReasons = recommendReasons,
            marketContext = marketContext,
            buyZone = buyZone,
            sellZone = sellZone,
            buyPlan = buyPlan,
            sellPlan = sellPlan,
        )
    }

    LazyColumn(
        modifier = modifier
            .fillMaxHeight()
            .background(Color.White),
        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 10.dp),
        verticalArrangement = Arrangement.spacedBy(9.dp),
    ) {
        item(key = "header") {
            StockHeader(
                stock = stock,
                isLive = isLive,
                holding = holding,
                isInWatchlist = isInWatchlist,
                canRemoveWatch = canRemoveWatch,
                onAddHolding = onAddHolding,
                onAddWatch = onAddWatch,
                onRemoveWatch = onRemoveWatch,
                onSetAlert = onSetAlert,
            )
        }
        item(key = "chart") {
            ChartCard(
                stock = stock,
                metrics = resolvedMetrics,
                tradeMarkers = tradeMarkers,
                recommendHistory = recommendHistory,
                modifier = Modifier
                    .fillMaxWidth()
                    .graphicsLayer(),
            )
        }
        item(key = "buy") { SuggestedBuyCard(plan = buyPlan) }
        item(key = "sell") { SuggestedSellCard(plan = sellPlan) }
        item(key = "reasons") {
            RecommendReasonsCard(
                reasons = researchReasons,
                risks = recommendRisks.ifEmpty {
                    listOfNotNull(resolvedMetrics.riskHint.takeIf { it.isNotBlank() })
                },
            )
        }
        item(key = "trend") { TrendSummary(resolvedMetrics) }
        item(key = "market") { MarketContextCard(marketContext) }
        item(key = "analysis") { AnalysisSummary(resolvedMetrics.analysis) }
        item(key = "advice") {
            CurrentAdviceCard(
                metrics = resolvedMetrics,
                buyZone = buyZone,
                sellZone = sellZone,
                reasons = researchReasons,
            )
        }
        item(key = "news") {
            LatestNewsCard(
                items = latestNews,
                loading = newsLoading,
                loadFailed = newsError,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun StockHeader(
    stock: StockItem,
    isLive: Boolean,
    holding: HoldingPosition?,
    isInWatchlist: Boolean,
    canRemoveWatch: Boolean,
    onAddHolding: () -> Unit,
    onAddWatch: () -> Unit,
    onRemoveWatch: () -> Unit,
    onSetAlert: () -> Unit,
) {
    val trendColor = when (stock.direction) {
        TrendDirection.UP -> StockColors.UpRed
        TrendDirection.DOWN -> StockColors.DownGreen
        TrendDirection.FLAT -> StockColors.Neutral
    }
    val outlinedColors = ButtonDefaults.outlinedButtonColors(
        contentColor = StockColors.TextPrimary,
    )
    val outlinedBorder = BorderStroke(1.dp, StockColors.Border)

    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val phone = isPhoneWidth(maxWidth)
        val nameSize = if (phone) 20.sp else 24.sp
        val priceSize = if (phone) 28.sp else 34.sp
        val changeSize = if (phone) 14.sp else 16.sp

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.Bottom) {
                        Text(
                            stock.name,
                            color = StockColors.TextPrimary,
                            fontSize = nameSize,
                            fontWeight = FontWeight.Bold,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f, fill = false),
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            stock.marketCode,
                            color = StockColors.TextSecondary,
                            fontSize = if (phone) 12.sp else 14.sp,
                            maxLines = 1,
                        )
                        if (isLive) {
                            Spacer(Modifier.width(6.dp))
                            Text(
                                "实时",
                                color = StockColors.UpRed,
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold,
                                modifier = Modifier
                                    .background(StockColors.UpSoft, RoundedCornerShape(4.dp))
                                    .padding(horizontal = 6.dp, vertical = 2.dp),
                            )
                        }
                    }

                    Spacer(Modifier.height(4.dp))

                    Row(verticalAlignment = Alignment.Bottom) {
                        Text(
                            stock.price,
                            color = trendColor,
                            fontSize = priceSize,
                            fontWeight = FontWeight.Bold,
                        )
                        Spacer(Modifier.width(if (phone) 10.dp else 16.dp))
                        Text(
                            "${stock.changeAmount}   ${stock.changePercent}",
                            color = trendColor,
                            fontSize = changeSize,
                            fontWeight = FontWeight.Medium,
                        )
                    }

                    Spacer(Modifier.height(6.dp))

                    Row(
                        modifier = Modifier.horizontalScroll(rememberScrollState()),
                        horizontalArrangement = Arrangement.spacedBy(7.dp),
                    ) {
                        stock.tags.take(4).forEachIndexed { index, tag ->
                            val background = when (index) {
                                0 -> Color(0xFFEFF6FF)
                                1 -> Color(0xFFF5F0FF)
                                2 -> Color(0xFFFFF4E8)
                                else -> Color(0xFFEEF8F1)
                            }
                            val foreground = when (index) {
                                0 -> Color(0xFF2563EB)
                                1 -> Color(0xFF7C3AED)
                                2 -> Color(0xFFEA580C)
                                else -> Color(0xFF16803A)
                            }

                            SmallPill(
                                text = tag,
                                foreground = foreground,
                                background = background,
                            )
                        }
                    }
                }
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(9.dp),
            ) {
                OutlinedButton(
                    onClick = if (isInWatchlist) onRemoveWatch else onAddWatch,
                    enabled = !isInWatchlist || canRemoveWatch,
                    modifier = Modifier.height(34.dp),
                    colors = outlinedColors,
                    border = outlinedBorder,
                ) {
                    Text(
                        if (isInWatchlist) "★ 移出自选" else "☆ 添加自选",
                        color = if (isInWatchlist && canRemoveWatch) StockColors.DownGreen else StockColors.TextPrimary,
                        fontSize = 12.sp,
                        maxLines = 1,
                        softWrap = false,
                    )
                }
                OutlinedButton(
                    onClick = onSetAlert,
                    modifier = Modifier.height(34.dp),
                    colors = outlinedColors,
                    border = outlinedBorder,
                ) {
                    val hasAlert = holding?.alertPrice != null || holding?.alertPnlPct != null
                    Text(
                        if (hasAlert) "🔔 已设提醒" else "🔔 设置提醒",
                        color = if (hasAlert) StockColors.PrimaryBlue else StockColors.TextPrimary,
                        fontSize = 12.sp,
                        maxLines = 1,
                        softWrap = false,
                    )
                }
                Button(
                    onClick = onAddHolding,
                    modifier = Modifier.height(34.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = StockColors.UpRed,
                        contentColor = Color.White,
                    ),
                ) {
                    Text(
                        if (holding != null) "✎ 编辑持仓" else "+ 添加持仓",
                        color = Color.White,
                        fontSize = 12.sp,
                        maxLines = 1,
                        softWrap = false,
                    )
                }
            }
        }
    }
}

@Composable
private fun TrendSummary(metrics: StockTrendMetrics) {
    AppCard(
        modifier = Modifier.fillMaxWidth()
    ) {
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val phone = isPhoneWidth(maxWidth)
            val riskColor = when {
                metrics.riskLevel.contains("高") -> StockColors.UpRed
                metrics.riskLevel.contains("低") -> StockColors.DownGreen
                else -> StockColors.Orange
            }
            val row = listOf(
                Triple("当前状态", metrics.status, StockColors.UpRed),
                Triple("当前位置", metrics.position, StockColors.TextSecondary),
                Triple("短期压力", metrics.resistance, StockColors.UpRed),
                Triple("短期支撑", metrics.support, StockColors.DownGreen),
                Triple("技术偏多", metrics.upsideProb, StockColors.UpRed),
                Triple("技术偏弱", metrics.weakenProb, StockColors.DownGreen),
                Triple("风险等级", metrics.riskLevel, riskColor)
            )

            if (phone) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState())
                        .padding(horizontal = 4.dp, vertical = 10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    row.forEach { item ->
                        Column(
                            modifier = Modifier.width(72.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            Text(
                                item.first,
                                color = StockColors.TextSecondary,
                                fontSize = 11.sp,
                                maxLines = 1,
                                softWrap = false,
                            )
                            Spacer(Modifier.height(4.dp))
                            Text(
                                item.second,
                                color = item.third,
                                fontSize = 13.sp,
                                fontWeight = FontWeight.SemiBold,
                                maxLines = 1,
                                softWrap = false,
                                overflow = TextOverflow.Ellipsis,
                            )
                        }
                    }
                }
            } else {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 4.dp, vertical = 10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    row.forEachIndexed { index, item ->
                        Column(
                            modifier = Modifier.weight(1f),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                item.first,
                                color = StockColors.TextSecondary,
                                fontSize = 11.sp,
                                maxLines = 1,
                            )
                            Spacer(Modifier.height(4.dp))
                            Text(
                                item.second,
                                color = item.third,
                                fontSize = 13.sp,
                                fontWeight = FontWeight.SemiBold,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MarketContextCard(context: StockMarketContext) {
    AppCard(
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 8.dp),
            verticalArrangement = Arrangement.spacedBy(7.dp),
        ) {
            Text(
                "资金 · 行情 · 板块",
                color = StockColors.TextPrimary,
                fontSize = 12.sp,
                fontWeight = FontWeight.Bold,
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(5.dp),
            ) {
                ContextMetric("总市值", displayMoney(context.totalMarketCap), StockColors.TextPrimary)
                ContextMetric("今日成交额", displayMoney(context.turnoverAmount), StockColors.TextPrimary)
                ContextMetric("今日主力", displaySignedMoney(context.todayMainNet), flowColor(context.todayMainNet))
                ContextMetric("近5日主力", displaySignedMoney(context.recentMainNet), flowColor(context.recentMainNet))
                ContextMetric(
                    "换手率",
                    context.turnoverRate?.let { "${formatDecimal(it)}%" } ?: "--",
                    if ((context.turnoverRate ?: 0.0) >= 15.0) StockColors.UpRed else StockColors.TextPrimary,
                )
                ContextMetric("市场", context.market.ifBlank { "--" }, StockColors.PrimaryBlue)
            }
            val concepts = context.concepts.take(4).joinToString("、").ifBlank { "--" }
            val strong = context.strongSectors.take(4).joinToString("、") {
                "${it.name}${if (it.changePercent > 0) "+" else ""}${formatDecimal(it.changePercent)}%"
            }.ifBlank { "--" }
            Text(
                "行业 ${context.industry.ifBlank { "--" }}　概念 $concepts",
                color = StockColors.TextSecondary,
                fontSize = 10.sp,
            )
            Text(
                "板块涨幅居前　$strong",
                color = StockColors.UpRed,
                fontSize = 10.sp,
            )
        }
    }
}

@Composable
private fun RowScope.ContextMetric(
    label: String,
    value: String,
    color: Color,
) {
    Column(
        modifier = Modifier.weight(1f),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(label, color = StockColors.TextSecondary, fontSize = 9.sp, maxLines = 1)
        Text(
            value,
            color = color,
            fontSize = 11.sp,
            fontWeight = FontWeight.Bold,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

private fun displayMoney(value: Double?): String {
    if (value == null) return "--"
    val absolute = abs(value)
    return when {
        absolute >= 100_000_000 -> "${formatDecimal(absolute / 100_000_000)}亿"
        absolute >= 10_000 -> "${formatDecimal(absolute / 10_000)}万"
        else -> formatDecimal(absolute, 0)
    }
}

private fun displaySignedMoney(value: Double?): String {
    if (value == null) return "--"
    val sign = when {
        value > 0.0 -> "+"
        value < 0.0 -> "-"
        else -> ""
    }
    return sign + displayMoney(value)
}

private fun flowColor(value: Double?): Color = when {
    value == null -> StockColors.TextSecondary
    value > 0.0 -> StockColors.UpRed
    value < 0.0 -> StockColors.DownGreen
    else -> StockColors.Neutral
}

@Composable
private fun AnalysisSummary(analysis: String) {
    AppCard(
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
Text("↗", color = StockColors.UpRed, fontSize = 20.sp, fontWeight = FontWeight.Bold)
            Spacer(Modifier.width(10.dp))
            Text(
                text = analysis,
                color = StockColors.TextPrimary,
                fontSize = 13.sp,
                lineHeight = 20.sp,
            )
        }
    }
}

@Composable
private fun SuggestedBuyCard(plan: BuyZonePlan?) {
    if (plan == null || plan.low <= 0 || plan.high <= 0) return
    val confText = plan.confidence.takeIf { it > 0 }?.let { "置信 ${(it * 100).toInt()}%" }
    AppCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        "建议买入价格",
                        color = StockColors.UpRed,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                    )
                    Spacer(Modifier.height(2.dp))
                    Text(
                        listOfNotNull(
                            plan.method.takeIf { it.isNotBlank() },
                            confText,
                            "研究参考",
                        ).joinToString(" · "),
                        color = StockColors.TextSecondary,
                        fontSize = 11.sp,
                    )
                }
                Text(
                    "${formatDecimal(plan.low)}–${formatDecimal(plan.high)}",
                    color = StockColors.UpRed,
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                )
            }
            plan.reasons.forEach { reason ->
                Text(
                    "· $reason",
                    color = StockColors.TextPrimary,
                    fontSize = 11.sp,
                    lineHeight = 16.sp,
                )
            }
        }
    }
}

@Composable
private fun SuggestedSellCard(plan: SellZonePlan?) {
    if (plan == null || plan.low <= 0 || plan.high <= 0) return
    val confText = plan.confidence.takeIf { it > 0 }?.let { "置信 ${(it * 100).toInt()}%" }
    AppCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        "建议卖出价格",
                        color = StockColors.DownGreen,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                    )
                    Spacer(Modifier.height(2.dp))
                    Text(
                        listOfNotNull(
                            plan.method.takeIf { it.isNotBlank() },
                            confText,
                            "研究参考",
                        ).joinToString(" · "),
                        color = StockColors.TextSecondary,
                        fontSize = 11.sp,
                    )
                }
                Text(
                    "${formatDecimal(plan.low)}–${formatDecimal(plan.high)}",
                    color = StockColors.DownGreen,
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                )
            }
            plan.reasons.forEach { reason ->
                Text(
                    "· $reason",
                    color = StockColors.TextPrimary,
                    fontSize = 11.sp,
                    lineHeight = 16.sp,
                )
            }
        }
    }
}

@Composable
private fun RecommendReasonsCard(
    reasons: List<String>,
    risks: List<String>,
) {
    if (reasons.isEmpty() && risks.isEmpty()) return
    AppCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Text(
                "推荐理由",
                color = StockColors.TextPrimary,
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold,
            )
            reasons.forEachIndexed { index, reason ->
                Row(modifier = Modifier.fillMaxWidth()) {
                    Text(
                        "${index + 1}.",
                        color = StockColors.PrimaryBlue,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.width(22.dp),
                    )
                    Text(
                        reason,
                        color = StockColors.TextPrimary,
                        fontSize = 12.sp,
                        lineHeight = 18.sp,
                        modifier = Modifier.weight(1f),
                    )
                }
            }
            if (risks.isNotEmpty()) {
                Spacer(Modifier.height(2.dp))
                Text(
                    "风险提示",
                    color = StockColors.Orange,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                risks.forEach { risk ->
                    Text(
                        "· $risk",
                        color = StockColors.TextSecondary,
                        fontSize = 11.sp,
                        lineHeight = 16.sp,
                    )
                }
            }
            Text(
                "以上为程序量化与技术面综合理由，仅供研究参考，不构成投资建议。",
                color = StockColors.Neutral,
                fontSize = 10.sp,
            )
        }
    }
}

@Composable
private fun CurrentAdviceCard(
    metrics: StockTrendMetrics,
    buyZone: Pair<Double, Double>? = null,
    sellZone: Pair<Double, Double>? = null,
    reasons: List<String> = emptyList(),
) {
    val titleColor = when {
        metrics.adviceTitle.contains("风险") || metrics.adviceTitle.contains("观望") ->
            StockColors.DownGreen
        metrics.adviceTitle.contains("关注") || metrics.adviceTitle.contains("共振") ->
            StockColors.UpRed
        else -> StockColors.Orange
    }
    AppCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(7.dp),
        ) {
            Text(
                "当前股票建议：${metrics.adviceTitle}",
                color = titleColor,
                fontSize = 16.sp,
                fontWeight = FontWeight.Bold,
            )
            if (buyZone != null) {
                Text(
                    "建议买入：${formatDecimal(buyZone.first)}–${formatDecimal(buyZone.second)}",
                    color = StockColors.UpRed,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
            if (sellZone != null) {
                Text(
                    "建议卖出：${formatDecimal(sellZone.first)}–${formatDecimal(sellZone.second)}",
                    color = StockColors.DownGreen,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
            if (reasons.isNotEmpty()) {
                Text(
                    "推荐理由（共${reasons.size}条）",
                    color = StockColors.TextPrimary,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                reasons.forEach { reason ->
                    Text(
                        "· $reason",
                        color = StockColors.TextPrimary,
                        fontSize = 12.sp,
                        lineHeight = 18.sp,
                    )
                }
            }
            Text(
                "综合判断：${metrics.positionDesc}",
                color = StockColors.TextPrimary,
                fontSize = 12.sp,
                lineHeight = 19.sp,
            )
            Text(
                "参考方案：${metrics.buyPlan}",
                color = StockColors.TextPrimary,
                fontSize = 12.sp,
                lineHeight = 19.sp,
            )
            Text(
                "风险提示：${metrics.riskHint}",
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
                lineHeight = 18.sp,
            )
        }
    }
}

private fun buildResearchReasons(
    stock: StockItem,
    metrics: StockTrendMetrics,
    scan: QuantScanItemDto?,
    recommendReasons: List<String>,
    marketContext: StockMarketContext,
    buyZone: Pair<Double, Double>?,
    sellZone: Pair<Double, Double>?,
    buyPlan: BuyZonePlan? = null,
    sellPlan: SellZonePlan? = null,
): List<String> {
    val out = linkedSetOf<String>()
    recommendReasons.forEach { r ->
        val t = r.trim()
        if (t.isNotEmpty()) out += t
    }
    if (scan != null) {
        val signalLabel = when (scan.signal.uppercase()) {
            "BUY" -> "买入"
            "SELL" -> "卖出"
            else -> "观望"
        }
        if (scan.reason.isNotBlank()) {
            out += "量化信号（$signalLabel）：${scan.reason.trim()}"
        } else {
            out += "量化信号：$signalLabel"
        }
        if (scan.buyVotes + scan.sellVotes > 0) {
            out += "多策略共识投票：买${scan.buyVotes} / 卖${scan.sellVotes}"
        }
    }
    out += "技术结论：${metrics.adviceTitle}"
    out += "当前状态 ${metrics.status}，位置 ${metrics.position}，风险等级 ${metrics.riskLevel}"
    out += "短期压力 ${metrics.resistance}，短期支撑 ${metrics.support}"
    out += "技术偏多 ${metrics.upsideProb}，技术偏弱 ${metrics.weakenProb}"
    metrics.analysis
        .split('；', ';', '。')
        .map { it.trim().trimStart('，', ',') }
        .filter { it.length >= 4 }
        .forEach { out += it }
    if (metrics.positionDesc.isNotBlank()) out += metrics.positionDesc.trim()
    if (metrics.buyPlan.isNotBlank()) out += "参考方案：${metrics.buyPlan.trim()}"
    if (buyPlan != null && buyPlan.low > 0 && buyPlan.high > 0) {
        out += "建议买入价格 ${formatDecimal(buyPlan.low)}–${formatDecimal(buyPlan.high)}" +
            if (buyPlan.method.isNotBlank()) "（${buyPlan.method}）" else ""
        buyPlan.reasons.take(4).forEach { r ->
            val t = r.trim()
            if (t.isNotEmpty()) out += t
        }
    } else if (buyZone != null) {
        out += "建议买入价格 ${formatDecimal(buyZone.first)}–${formatDecimal(buyZone.second)}"
    }
    if (sellPlan != null && sellPlan.low > 0 && sellPlan.high > 0) {
        out += "建议卖出价格 ${formatDecimal(sellPlan.low)}–${formatDecimal(sellPlan.high)}" +
            if (sellPlan.method.isNotBlank()) "（${sellPlan.method}）" else ""
        sellPlan.reasons.take(4).forEach { r ->
            val t = r.trim()
            if (t.isNotEmpty()) out += t
        }
    } else if (sellZone != null) {
        out += "建议卖出价格 ${formatDecimal(sellZone.first)}–${formatDecimal(sellZone.second)}"
    }
    marketContext.todayMainNet?.let {
        out += "今日主力净流入 ${displaySignedMoney(it)}"
    }
    marketContext.recentMainNet?.let {
        out += "近5日主力净流入 ${displaySignedMoney(it)}"
    }
    marketContext.turnoverRate?.let {
        out += "换手率 ${formatDecimal(it)}%"
    }
    if (marketContext.industry.isNotBlank() && marketContext.industry != "—") {
        out += "所属行业：${marketContext.industry}"
    }
    if (marketContext.concepts.isNotEmpty()) {
        out += "相关概念：${marketContext.concepts.take(4).joinToString("、")}"
    }
    if (marketContext.strongSectors.isNotEmpty()) {
        out += "强势板块：${
            marketContext.strongSectors.take(3).joinToString("、") {
                "${it.name}${if (it.changePercent > 0) "+" else ""}${formatDecimal(it.changePercent)}%"
            }
        }"
    }
    if (stock.tags.isNotEmpty()) {
        out += "标签：${stock.tags.joinToString("、")}"
    }
    return out.toList()
}

/** 扫描卖区优先；否则用本地日 K 历史推导；仍无则不展示。 */
private fun resolveResearchSellPlan(
    scan: QuantScanItemDto?,
    localPlan: SellZonePlan?,
): SellZonePlan? {
    if (scan != null && scan.sellLow > 0 && scan.sellHigh > 0) {
        return SellZonePlan(
            low = scan.sellLow,
            high = scan.sellHigh,
            confidence = scan.sellConfidence,
            method = scan.sellMethod.ifBlank { "扫描多锚点汇合" },
            reasons = scan.sellReasons,
        )
    }
    return localPlan?.takeIf { it.low > 0 && it.high > 0 }
}

private fun resolveResearchBuyPlan(
    scan: QuantScanItemDto?,
    localPlan: BuyZonePlan?,
): BuyZonePlan? {
    if (scan != null && scan.buyLow > 0 && scan.buyHigh > 0) {
        return BuyZonePlan(
            low = scan.buyLow,
            high = scan.buyHigh,
            confidence = scan.buyConfidence,
            method = scan.buyMethod.ifBlank { "扫描多锚点支撑" },
            reasons = scan.buyReasons,
        )
    }
    return localPlan?.takeIf { it.low > 0 && it.high > 0 }
}

@Composable
private fun ChartCard(
    stock: StockItem,
    metrics: StockTrendMetrics,
    tradeMarkers: List<org.zq.astrock_ai.ui.research.gushi.model.ChartTradeMarker> = emptyList(),
    recommendHistory: List<org.zq.astrock_ai.ui.research.gushi.data.RecommendHistorySnapshot> = emptyList(),
    modifier: Modifier = Modifier,
) {
    val repository = remember { MarketRepository() }
    var period by remember { mutableStateOf(ChartPeriod.INTRADAY) }
    var candles by remember { mutableStateOf<List<Candle>>(emptyList()) }
    var live by remember { mutableStateOf(false) }
    var expanded by remember { mutableStateOf(false) }

    LaunchedEffect(stock.code, period) {
        candles = emptyList()
        live = false
        while (true) {
            val response = repository.loadKline(stock.code, period)
            if (response.bars.isNotEmpty()) {
                candles = response.bars.toCandles()
                live = true
            } else {
                val base = stock.price.replace(",", "").toDoubleOrNull() ?: 5.18
                candles = DemoData.candlesForPeriod(
                    period = period,
                    seed = stock.code.takeLast(2).toIntOrNull() ?: 0,
                    baseClose = base,
                )
                live = false
            }
            // 分时降频，减少研究页滑动时的重绘压力
            delay(if (period.isLineChart) 30_000 else 60_000)
        }
    }

    val quoteChangePercent = stock.changePercent
        .filter { it.isDigit() || it == '.' || it == '-' }
        .toDoubleOrNull()
    val referenceClose = if (candles.isNotEmpty()) {
        quoteChangePercent
            ?.takeIf { it > -100.0 }
            ?.let { candles.last().close / (1.0 + it / 100.0) }
    } else {
        null
    }
    val chartMarkers = if (period.isLineChart) emptyList() else tradeMarkers
    val recommendMarkers = remember(recommendHistory, stock.code) {
        recommendMarkersFor(recommendHistory, stock.code)
    }

    AppCard(
        modifier = modifier
            .fillMaxWidth()
            .graphicsLayer(),
    ) {
        Column(Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(42.dp)
                    .padding(horizontal = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Row(
                    modifier = Modifier
                        .weight(1f)
                        .horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    ChartPeriod.entries.forEach { item ->
                        val selected = period == item
                        Text(
                            text = item.label,
                            color = if (selected) StockColors.UpRed else StockColors.TextSecondary,
                            fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                            fontSize = 13.sp,
                            modifier = Modifier
                                .clickable { period = item }
                                .background(
                                    color = if (selected) StockColors.UpSoft else Color.Transparent,
                                    shape = RoundedCornerShape(6.dp),
                                )
                                .padding(horizontal = 8.dp, vertical = 5.dp),
                        )
                    }
                }
                Text(
                    if (live) "实时" else "演示",
                    color = if (live) StockColors.UpRed else StockColors.TextSecondary,
                    fontSize = 11.sp,
                )
                // 放大图标：弹框全屏查看
                Text(
                    "⛶",
                    color = StockColors.PrimaryBlue,
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center,
                    modifier = Modifier
                        .size(32.dp)
                        .background(Color(0xFFE8F0FE), RoundedCornerShape(8.dp))
                        .clickable(enabled = candles.isNotEmpty()) { expanded = true }
                        .padding(top = 2.dp),
                )
            }

            HorizontalDivider(color = StockColors.Border)

            // 分时/五日更偏高；日K等略扁。原先 1.9 在手机上过矮。
            val chartAspect = if (period.isLineChart) 1.22f else 1.55f
            val chartMinHeight = if (period.isLineChart) 280.dp else 220.dp

            if (candles.isEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = chartMinHeight)
                        .aspectRatio(chartAspect),
                    contentAlignment = Alignment.Center,
                ) {
                    Text("加载${period.label}走势…", color = StockColors.TextSecondary, fontSize = 13.sp)
                }
            } else {
                StockChart(
                    candles = candles,
                    support = metrics.support.toDoubleOrNull(),
                    resistance = metrics.resistance.toDoubleOrNull(),
                    showDecorations = false,
                    lineMode = period.isLineChart,
                    fiveDayMode = period == ChartPeriod.FIVE_DAY,
                    referenceClose = referenceClose,
                    tradeMarkers = chartMarkers,
                    recommendMarkers = recommendMarkers,
                    chartPeriod = period,
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = chartMinHeight)
                        .aspectRatio(chartAspect),
                )
            }
        }
    }

    if (expanded && candles.isNotEmpty()) {
        ChartExpandDialog(
            stockName = stock.name,
            stockCode = stock.code,
            period = period,
            onPeriodChange = { period = it },
            candles = candles,
            live = live,
            support = metrics.support.toDoubleOrNull(),
            resistance = metrics.resistance.toDoubleOrNull(),
            referenceClose = referenceClose,
            tradeMarkers = chartMarkers,
            recommendMarkers = recommendMarkers,
            onDismiss = { expanded = false },
        )
    }
}

@Composable
private fun ChartExpandDialog(
    stockName: String,
    stockCode: String,
    period: ChartPeriod,
    onPeriodChange: (ChartPeriod) -> Unit,
    candles: List<Candle>,
    live: Boolean,
    support: Double?,
    resistance: Double?,
    referenceClose: Double?,
    tradeMarkers: List<org.zq.astrock_ai.ui.research.gushi.model.ChartTradeMarker>,
    recommendMarkers: List<org.zq.astrock_ai.ui.research.gushi.model.ChartRecommendMarker> = emptyList(),
    onDismiss: () -> Unit,
) {
    val minBars = min(12, candles.size).coerceAtLeast(1)
    var visibleBars by remember(candles.size, period) {
        mutableIntStateOf(
            if (candles.size > 80) 60.coerceAtMost(candles.size) else candles.size,
        )
    }
    val shape = RoundedCornerShape(14.dp)

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth(0.96f)
                .widthIn(max = 920.dp)
                .fillMaxHeight(0.92f)
                .background(Color.White, shape)
                .border(BorderStroke(1.dp, StockColors.Border), shape)
                .padding(12.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        "$stockName 走势",
                        color = StockColors.TextPrimary,
                        fontWeight = FontWeight.Bold,
                        fontSize = 17.sp,
                        maxLines = 1,
                    )
                    Text(
                        "$stockCode · ${if (live) "真实行情" else "演示"} · ${period.label}",
                        color = StockColors.TextSecondary,
                        fontSize = 11.sp,
                    )
                }
                Text(
                    "关闭",
                    color = StockColors.TextSecondary,
                    fontSize = 14.sp,
                    modifier = Modifier
                        .clickable(onClick = onDismiss)
                        .padding(8.dp),
                )
            }

            Spacer(Modifier.height(6.dp))

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                ChartPeriod.entries.forEach { item ->
                    val selected = period == item
                    Text(
                        item.label,
                        color = if (selected) StockColors.UpRed else StockColors.TextSecondary,
                        fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                        fontSize = 13.sp,
                        modifier = Modifier
                            .clickable { onPeriodChange(item) }
                            .background(
                                if (selected) StockColors.UpSoft else Color(0xFFF3F4F6),
                                RoundedCornerShape(8.dp),
                            )
                            .padding(horizontal = 10.dp, vertical = 6.dp),
                    )
                }
            }

            Spacer(Modifier.height(8.dp))

            StockChart(
                candles = candles,
                support = support,
                resistance = resistance,
                showDecorations = false,
                lineMode = period.isLineChart,
                fiveDayMode = period == ChartPeriod.FIVE_DAY,
                referenceClose = referenceClose,
                tradeMarkers = if (period.isLineChart) emptyList() else tradeMarkers,
                recommendMarkers = recommendMarkers,
                chartPeriod = period,
                visibleBars = visibleBars,
                onVisibleBarsChange = { visibleBars = it },
                showZoomControls = false,
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
            )

            Spacer(Modifier.height(8.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                ChartZoomButton(
                    label = "缩小",
                    enabled = visibleBars < candles.size,
                    modifier = Modifier.weight(1f),
                    onClick = {
                        visibleBars = min(
                            candles.size,
                            max(visibleBars + 1, visibleBars * 4 / 3),
                        )
                    },
                )
                Text(
                    "$visibleBars / ${candles.size}",
                    color = StockColors.TextSecondary,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                ChartZoomButton(
                    label = "放大",
                    enabled = visibleBars > minBars,
                    modifier = Modifier.weight(1f),
                    onClick = {
                        visibleBars = max(
                            minBars,
                            min(visibleBars - 1, visibleBars * 3 / 4),
                        )
                    },
                )
                ChartZoomButton(
                    label = "全览",
                    enabled = visibleBars < candles.size,
                    modifier = Modifier.weight(1f),
                    onClick = { visibleBars = candles.size },
                )
            }
        }
    }
}

@Composable
private fun ChartZoomButton(
    label: String,
    enabled: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Text(
        label,
        color = if (enabled) Color.White else StockColors.Neutral,
        fontSize = 14.sp,
        fontWeight = FontWeight.SemiBold,
        textAlign = TextAlign.Center,
        modifier = modifier
            .background(
                if (enabled) StockColors.PrimaryBlue else Color(0xFFE5E7EB),
                RoundedCornerShape(10.dp),
            )
            .clickable(enabled = enabled, onClick = onClick)
            .padding(vertical = 12.dp),
    )
}

@Composable
private fun LatestNewsCard(
    items: List<NewsItem>,
    loading: Boolean,
    loadFailed: Boolean,
    modifier: Modifier = Modifier
) {
    var selectedTab by remember { mutableStateOf("最新动态") }
    val uriHandler = LocalUriHandler.current

    AppCard(modifier = modifier) {
        Column(Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(46.dp)
                    .padding(horizontal = 13.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(26.dp)
            ) {
                listOf("最新动态", "新闻", "公告", "资金", "财务", "研报").forEach { tab ->
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        modifier = Modifier.clickable { selectedTab = tab },
                    ) {
                        Text(
                            tab,
                            color = if (selectedTab == tab) StockColors.TextPrimary else StockColors.TextSecondary,
                            fontWeight = if (selectedTab == tab) FontWeight.Bold else FontWeight.Normal,
                            fontSize = 14.sp
                        )
                        Spacer(Modifier.height(8.dp))
                        if (selectedTab == tab) {
                            Spacer(
                                Modifier
                                    .width(54.dp)
                                    .height(3.dp)
                                    .background(StockColors.UpRed, RoundedCornerShape(3.dp))
                            )
                        } else {
                            Spacer(Modifier.height(3.dp))
                        }
                    }
                }
            }

            HorizontalDivider(color = StockColors.Border)

            val visibleItems = items.filter { news ->
                when (selectedTab) {
                    "新闻" -> news.type == NewsType.NEWS
                    "公告" -> news.type == NewsType.ANNOUNCEMENT
                    "资金" -> news.type == NewsType.CAPITAL
                    "财务" -> news.type == NewsType.FINANCIAL
                    "研报" -> news.type == NewsType.RESEARCH
                    else -> true
                }
            }

            if (visibleItems.isEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 24.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        when {
                            loading -> "正在获取最新消息…"
                            loadFailed -> "最新消息获取失败，将在一分钟后自动重试"
                            else -> "暂无最新${selectedTab.takeUnless { it == "最新动态" }.orEmpty()}消息"
                        },
                        color = StockColors.TextSecondary,
                        fontSize = 12.sp,
                    )
                }
            } else {
                visibleItems.take(20).forEach { news ->
                    NewsRow(
                        item = news,
                        onClick = {
                            if (news.url.isNotBlank()) {
                                runCatching { uriHandler.openUri(news.url) }
                            }
                        },
                    )
                    HorizontalDivider(
                        modifier = Modifier.padding(horizontal = 13.dp),
                        color = Color(0xFFF0F1F4),
                    )
                }
            }
        }
    }
}

@Composable
private fun NewsRow(
    item: NewsItem,
    onClick: () -> Unit,
) {
    val typeLabel = when (item.type) {
        NewsType.NEWS -> "新闻"
        NewsType.CAPITAL -> "资金"
        NewsType.ANNOUNCEMENT -> "公告"
        NewsType.FINANCIAL -> "财务"
        NewsType.RESEARCH -> "研报"
    }

    val typeColor = when (item.type) {
        NewsType.NEWS -> Color(0xFFF97316)
        NewsType.CAPITAL -> Color(0xFF2563EB)
        NewsType.ANNOUNCEMENT -> Color(0xFF16A34A)
        NewsType.FINANCIAL -> Color(0xFF0891B2)
        NewsType.RESEARCH -> Color(0xFF9333EA)
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(enabled = item.url.isNotBlank(), onClick = onClick)
            .padding(horizontal = 13.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            item.time,
            color = StockColors.TextSecondary,
            fontSize = 11.sp,
            maxLines = 1,
            modifier = Modifier.width(116.dp),
        )
        Text(
            typeLabel,
            color = typeColor,
            fontSize = 12.sp,
            modifier = Modifier
                .background(typeColor.copy(alpha = 0.10f), RoundedCornerShape(5.dp))
                .padding(horizontal = 7.dp, vertical = 3.dp)
        )
        Spacer(Modifier.width(10.dp))
        Text(
            item.title,
            modifier = Modifier.weight(1f),
            color = if (item.url.isNotBlank()) StockColors.PrimaryBlue else StockColors.TextPrimary,
            fontSize = 13.sp,
            maxLines = 2,
        )
        Text("来源：${item.source}", color = StockColors.TextSecondary, fontSize = 11.sp)
        Spacer(Modifier.width(12.dp))

        val impactColor = if (item.impact.contains("利好")) StockColors.UpRed else StockColors.Neutral
        Text(
            item.impact,
            color = impactColor,
            fontSize = 11.sp,
            modifier = Modifier
                .background(impactColor.copy(alpha = 0.08f), RoundedCornerShape(5.dp))
                .padding(horizontal = 8.dp, vertical = 3.dp)
        )
    }
}

private fun String.toDisplayTime(): String {
    val normalized = replace('T', ' ')
    return when {
        normalized.length >= 16 -> normalized.take(16)
        normalized.length >= 10 -> normalized.take(10)
        else -> normalized
    }
}
